import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  Chip,
  Alert,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AssignmentInd,
  Schedule,
  CheckCircle,
  Person,
  EventAvailable,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { getSubstituteAssignments } from '../utils/substituteUtils';
import {
  PageContainer,
  SectionHeader,
  StyledCard,
} from '../components/ui/StyledComponents';
import supabase from '../supabaseClient';
import CoverageRequestForm from '../components/CoverageRequestForm';
import SnackbarMessage from '../components/SnackbarMessage';
import dayjs from 'dayjs';

function SubstituteAssignmentsPage() {
  const { teacherId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Initialize tab value from URL parameter or default to 0
  const getInitialTabValue = () => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab === 'coverage') return 1;
    return 0; // default to 'substitute-work'
  };
  
  const [tabValue, setTabValue] = useState(getInitialTabValue);
  const [loading, setLoading] = useState(true);
  const [mySubstituteWork, setMySubstituteWork] = useState([]);
  const [myCoverageAssignments, setMyCoverageAssignments] = useState([]);
  const [stats, setStats] = useState({
    substituteWork: { total: 0, active: 0, completed: 0 },
    myCoverage: { total: 0, active: 0, completed: 0 },
  });

  // Coverage requests state
  const [coverageRequests, setCoverageRequests] = useState([]);
  const [coverageRequestsLoading, setCoverageRequestsLoading] = useState(true);
  const [showCoverageRequestForm, setShowCoverageRequestForm] = useState(false);
  const [editingCoverageRequest, setEditingCoverageRequest] = useState(null);
  const [coverageFilter, setCoverageFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const fetchCoverageRequests = useCallback(async () => {
    try {
      setCoverageRequestsLoading(true);
      const { data, error } = await supabase
        .from('coverage_requests')
        .select(`
          *,
          classes (
            id,
            name,
            shift
          ),
          substitute_teacher:substitute_teacher_id (
            id,
            first_name,
            last_name
          ),
          reviewed_by_admin:reviewed_by (
            id,
            first_name,
            last_name
          )
        `)
        .eq('teacher_id', teacherId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCoverageRequests(data || []);
    } catch (error) {
      console.error('Error fetching coverage requests:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load coverage requests',
        severity: 'error'
      });
    } finally {
      setCoverageRequestsLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    const fetchAllAssignments = async () => {
      if (!teacherId) return;
      
      try {
        setLoading(true);
        // Get assignments for the last 6 months and next 3 months
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 6);
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 3);
        
        const startDateStr = dayjs(startDate).format('YYYY-MM-DD');
        const endDateStr = dayjs(endDate).format('YYYY-MM-DD');
        
        const allAssignments = await getSubstituteAssignments(
          startDateStr,
          endDateStr
        );

        // Tab 1: Assignments where this teacher was the substitute
        const substituteWork = allAssignments.filter(
          assignment => assignment.substitute_teacher_id === teacherId
        );

        // Tab 2: Assignments where this teacher's classes were covered
        const myCoverage = allAssignments.filter(
          assignment => assignment.original_teacher_id === teacherId
        );

        const today = new Date();
        
        // Calculate stats for substitute work
        const activeSubstituteWork = substituteWork.filter(assignment => {
          const startDate = new Date(assignment.start_date);
          const endDate = new Date(assignment.end_date);
          return assignment.status === 'pending' && startDate <= today && endDate >= today;
        });
        const completedSubstituteWork = substituteWork.filter(
          assignment => assignment.status === 'completed'
        );

        // Calculate stats for my coverage
        const activeCoverage = myCoverage.filter(assignment => {
          const startDate = new Date(assignment.start_date);
          const endDate = new Date(assignment.end_date);
          return assignment.status === 'pending' && startDate <= today && endDate >= today;
        });
        const completedCoverage = myCoverage.filter(
          assignment => assignment.status === 'completed'
        );

        setMySubstituteWork(substituteWork);
        setMyCoverageAssignments(myCoverage);
        setStats({
          substituteWork: {
            total: substituteWork.length,
            active: activeSubstituteWork.length,
            completed: completedSubstituteWork.length,
          },
          myCoverage: {
            total: myCoverage.length,
            active: activeCoverage.length,
            completed: completedCoverage.length,
          },
        });
      } catch (error) {
        console.error('Error fetching assignments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllAssignments();
    fetchCoverageRequests();
  }, [teacherId, fetchCoverageRequests]);

  // Sync tab state with URL changes (browser back/forward)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    const newTabValue = tab === 'coverage' ? 1 : 0;
    if (newTabValue !== tabValue) {
      setTabValue(newTabValue);
    }
  }, [location.search, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Update URL parameter to persist tab state
    const urlParams = new URLSearchParams(location.search);
    if (newValue === 1) {
      urlParams.set('tab', 'coverage');
    } else {
      urlParams.delete('tab'); // default tab, no parameter needed
    }
    
    const newSearch = urlParams.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    navigate(newPath, { replace: true });
  };

  // Coverage request handlers
  const handleCreateCoverageRequest = () => {
    setEditingCoverageRequest(null);
    setShowCoverageRequestForm(true);
  };

  const handleEditCoverageRequest = (request) => {
    if (request.status !== 'pending') {
      setSnackbar({
        open: true,
        message: 'Only pending requests can be edited',
        severity: 'error'
      });
      return;
    }
    setEditingCoverageRequest(request);
    setShowCoverageRequestForm(true);
  };

  const handleDeleteCoverageRequest = async (request) => {
    if (request.status !== 'pending') {
      setSnackbar({
        open: true,
        message: 'Only pending requests can be deleted',
        severity: 'error'
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this coverage request?')) {
      try {
        setCoverageRequestsLoading(true);
        const { error } = await supabase
          .from('coverage_requests')
          .delete()
          .eq('id', request.id);

        if (error) throw error;

        // Remove the deleted request from state immediately for instant UI feedback
        setCoverageRequests(prev => prev.filter(req => req.id !== request.id));

        setSnackbar({
          open: true,
          message: 'Coverage request deleted successfully',
          severity: 'success'
        });

        // Also refresh the data from server to ensure consistency
        await fetchCoverageRequests();
      } catch (error) {
        console.error('Error deleting request:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete request',
          severity: 'error'
        });
        // Refresh data to ensure UI is in sync with server
        await fetchCoverageRequests();
      }
    }
  };

  const handleCoverageRequestFormSuccess = () => {
    fetchCoverageRequests();
    setShowCoverageRequestForm(false);
  };

  const handleCoverageRequestFormClose = () => {
    setShowCoverageRequestForm(false);
    setEditingCoverageRequest(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Active';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const currentStats = tabValue === 0 ? stats.substituteWork : stats.myCoverage;

  return (
    <PageContainer>
      <SectionHeader sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        My Substitute Assignments
      </SectionHeader>
      
      <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
        <Typography 
          variant="body1" 
          color="textSecondary" 
          sx={{ 
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          Track your substitute teaching work and monitor coverage for your classes.
        </Typography>

        {/* Tabs */}
        <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'fullWidth'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile={isMobile}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 1.5, sm: 2 },
                minWidth: { xs: 'auto', sm: 0 },
                px: { xs: 2, sm: 3 },
              },
            }}
          >
            <Tab
              icon={<Person sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />}
              label="My Substitute Work"
              iconPosition="start"
              sx={{ gap: { xs: 0.5, sm: 1 } }}
            />
            <Tab
              icon={<EventAvailable sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />}
              label="Coverage for My Classes"
              iconPosition="start"
              sx={{ gap: { xs: 0.5, sm: 1 } }}
            />
          </Tabs>
        </Paper>

        {/* Statistics Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
          <Grid item xs={12} sm={4}>
            <StyledCard
              sx={{
                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AssignmentInd sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, mr: { xs: 1.5, sm: 2 } }} />
                  <Box>
                    <Typography 
                      variant="h4" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                      }}
                    >
                      {loading ? '...' : currentStats.total}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        opacity: 0.9,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      Total Assignments
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={4}>
            <StyledCard
              sx={{
                background: 'linear-gradient(135deg, #e67e22 0%, #f39c12 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Schedule sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, mr: { xs: 1.5, sm: 2 } }} />
                  <Box>
                    <Typography 
                      variant="h4" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                      }}
                    >
                      {loading ? '...' : currentStats.active}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        opacity: 0.9,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      Active Now
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} sm={4}>
            <StyledCard
              sx={{
                background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, mr: { xs: 1.5, sm: 2 } }} />
                  <Box>
                    <Typography 
                      variant="h4" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                      }}
                    >
                      {loading ? '...' : currentStats.completed}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        opacity: 0.9,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      Completed
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Tab Content */}
        {tabValue === 0 ? (
          // My Substitute Work Tab
        <StyledCard>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
              <CircularProgress size={isMobile ? 32 : 40} />
            </Box>
            ) : mySubstituteWork.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 0 } }}>
                <Typography 
                  variant="h6" 
                  color="textSecondary" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  No substitute assignments found
                </Typography>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  You haven't been assigned as a substitute teacher yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                sx={{
                  overflowX: { xs: 'auto', sm: 'visible' },
                  '&::-webkit-scrollbar': {
                    height: { xs: '6px', sm: '0px' },
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                  },
                }}
              >
                <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Class
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>
                        Original Teacher
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Period
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>
                        Reason
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mySubstituteWork.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {assignment.classes?.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {assignment.classes?.shift}
                            </Typography>
                            {isMobile && (
                              <>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                  Teacher: {assignment.original_teacher?.first_name} {assignment.original_teacher?.last_name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                  Reason: {assignment.reason}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {assignment.original_teacher?.first_name} {assignment.original_teacher?.last_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {dayjs(assignment.start_date).format('MMM D')} - {dayjs(assignment.end_date).format('MMM D, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {dayjs(assignment.end_date).diff(dayjs(assignment.start_date), 'day') + 1} days
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {assignment.reason}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Chip
                            label={getStatusLabel(assignment.status)}
                            color={getStatusColor(assignment.status)}
                            size="small"
                            variant="filled"
                            sx={{
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              height: { xs: 20, sm: 24 },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledCard>
        ) : (
          // Coverage for My Classes Tab
          <Box>
            {/* Request Coverage Button */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'stretch', sm: 'center' }, 
              mb: { xs: 2, sm: 3 },
              gap: { xs: 2, sm: 0 },
            }}>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />}
                onClick={handleCreateCoverageRequest}
                fullWidth={isMobile}
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '10px 16px', sm: '12px 24px' },
                }}
              >
                Request Coverage
              </Button>
              
              <FormControl 
                size="small" 
                sx={{ 
                  minWidth: { xs: '100%', sm: 150 },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Filter</InputLabel>
                <Select
                  value={coverageFilter}
                  label="Filter"
                  onChange={(e) => setCoverageFilter(e.target.value)}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '& .MuiMenuItem-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                >
                  <MenuItem value="all">All Requests</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Coverage Requests Table */}
            <StyledCard>
              {coverageRequestsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
                  <CircularProgress size={isMobile ? 32 : 40} />
                </Box>
              ) : coverageRequests.filter(request => 
                coverageFilter === 'all' ? true : request.status === coverageFilter
              ).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 0 } }}>
              <Typography 
                variant="h6" 
                color="textSecondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                    No coverage requests found
              </Typography>
              <Typography 
                variant="body2" 
                color="textSecondary"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                    {coverageFilter === 'all' 
                      ? "You haven't submitted any coverage requests yet."
                      : `No ${coverageFilter} requests found.`
                }
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                overflowX: { xs: 'auto', sm: 'visible' },
                '&::-webkit-scrollbar': {
                  height: { xs: '6px', sm: '0px' },
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '3px',
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 900, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                          Class
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                          Period
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>
                          Reason
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>
                          Substitute
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coverageRequests.filter(request => 
                        coverageFilter === 'all' ? true : request.status === coverageFilter
                      ).map((request) => (
                        <TableRow key={request.id}>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {request.classes?.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {request.classes?.shift}
                              </Typography>
                              {isMobile && (
                                <>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                    Reason: {request.reason}
                                  </Typography>
                                  {request.substitute_teacher && (
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                      Substitute: {request.substitute_teacher.first_name} {request.substitute_teacher.last_name}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {dayjs(request.start_date).format('MMM D')} - {dayjs(request.end_date).format('MMM D, YYYY')}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {dayjs(request.end_date).diff(dayjs(request.start_date), 'day') + 1} day{dayjs(request.end_date).diff(dayjs(request.start_date), 'day') !== 0 ? 's' : ''}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {request.reason}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Chip
                              label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              color={request.status === 'pending' ? 'warning' : request.status === 'assigned' ? 'success' : request.status === 'rejected' ? 'error' : 'info'}
                              size="small"
                              variant="filled"
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 },
                              }}
                            />
                            {request.status === 'rejected' && request.rejection_reason && (
                              <Box sx={{ 
                                mt: 1, 
                                p: { xs: 1, sm: 1.5 }, 
                                backgroundColor: 'grey.100', 
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'error.main'
                              }}>
                                <Typography 
                                  variant="body2" 
                                  color="text.primary" 
                                  sx={{ 
                                    fontWeight: 'medium',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  }}
                                >
                                  Rejection Reason:
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    mt: 0.5,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  }}
                                >
                                  {request.rejection_reason}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                            {request.substitute_teacher ? (
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {request.substitute_teacher.first_name} {request.substitute_teacher.last_name}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Not assigned
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'center' }}>
                              {request.description && (
                                <Tooltip title={request.description}>
                                  <IconButton size="small" sx={{ padding: { xs: 0.5, sm: 1 } }}>
                                    <InfoIcon fontSize={isMobile ? 'small' : 'medium'} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {request.status === 'pending' && (
                                <>
                                  <Tooltip title="Edit Request">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleEditCoverageRequest(request)}
                                      sx={{ padding: { xs: 0.5, sm: 1 } }}
                                    >
                                      <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Request">
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => handleDeleteCoverageRequest(request)}
                                      sx={{ padding: { xs: 0.5, sm: 1 } }}
                                    >
                                      <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </StyledCard>

            {/* Coverage assignments for this teacher's classes */}
            <Typography 
              variant="h6" 
              sx={{ 
                mt: { xs: 3, sm: 4 }, 
                mb: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              Coverage Assignments for Your Classes
            </Typography>
            <StyledCard>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
                  <CircularProgress size={isMobile ? 32 : 40} />
                </Box>
              ) : myCoverageAssignments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 0 } }}>
                  <Typography 
                    variant="h6" 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    No coverage assignments found
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    No substitute coverage has been arranged for your classes.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    overflowX: { xs: 'auto', sm: 'visible' },
                    '&::-webkit-scrollbar': {
                      height: { xs: '6px', sm: '0px' },
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '3px',
                    },
                  }}
                >
                  <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                          Class
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>
                          Substitute Teacher
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                          Period
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>
                          Reason
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                          Status
                        </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                      {myCoverageAssignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {assignment.classes?.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {assignment.classes?.shift}
                          </Typography>
                          {isMobile && (
                            <>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                Teacher: {assignment.substitute_teacher?.first_name} {assignment.substitute_teacher?.last_name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                Reason: {assignment.reason}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {assignment.substitute_teacher?.first_name} {assignment.substitute_teacher?.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {dayjs(assignment.start_date).format('MMM D')} - {dayjs(assignment.end_date).format('MMM D, YYYY')}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {dayjs(assignment.end_date).diff(dayjs(assignment.start_date), 'day') + 1} days
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {assignment.reason}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Chip
                          label={getStatusLabel(assignment.status)}
                          color={getStatusColor(assignment.status)}
                          size="small"
                          variant="filled"
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: 20, sm: 24 },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </StyledCard>
          </Box>
        )}

        {/* Help Information */}
        <Alert severity="info" sx={{ mt: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="body2"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            <strong>Need help with substitute assignments?</strong><br />
            {tabValue === 0 
              ? "Contact your administrator to report issues with substitute coverage or to request new substitute arrangements."
              : "Use the 'Request Coverage' button to submit new coverage requests for your classes."
            }
          </Typography>
        </Alert>
      </Box>

      {/* Coverage Request Form */}
      <CoverageRequestForm
        open={showCoverageRequestForm}
        onClose={handleCoverageRequestFormClose}
        onSuccess={handleCoverageRequestFormSuccess}
        existingRequest={editingCoverageRequest}
      />

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </PageContainer>
  );
}

export default SubstituteAssignmentsPage;
